import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, CircleAlert, Info, X, XCircle } from 'lucide-react';
import { duracaoDoToast } from './toast-duracao';
import '../../styles/toast.css';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';
export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}
export interface ToastOptions {
  readonly variant?: ToastVariant;
  readonly title: string;
  readonly description?: string;
  readonly duration?: number;
  readonly action?: ToastAction;
}
interface ToastEntry extends Required<Pick<ToastOptions, 'variant' | 'title' | 'duration'>> {
  readonly id: string;
  readonly description?: string;
  readonly action?: ToastAction;
}
interface ToastApi {
  readonly toast: (options: ToastOptions) => string;
  readonly success: (options: Omit<ToastOptions, 'variant'>) => string;
  readonly info: (options: Omit<ToastOptions, 'variant'>) => string;
  readonly warning: (options: Omit<ToastOptions, 'variant'>) => string;
  readonly error: (options: Omit<ToastOptions, 'variant'>) => string;
  readonly dismiss: (id: string) => void;
}
const ToastContext = createContext<ToastApi | null>(null);
const MAX_VISIBLE = 3;
const VARIANTS = {
  success: { Icon: CheckCircle2, label: 'Sucesso', urgent: false },
  info: { Icon: Info, label: 'Informação', urgent: false },
  warning: { Icon: CircleAlert, label: 'Atenção', urgent: false },
  error: { Icon: XCircle, label: 'Erro', urgent: true },
};

function ToastItem({ entry, dismiss }: { readonly entry: ToastEntry; readonly dismiss: (id: string) => void }): ReactElement {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const remaining = useRef(entry.duration);
  const actionExecuted = useRef(false);
  const { Icon, label, urgent } = VARIANTS[entry.variant];
  const paused = hovered || focused;

  // Pausa mantém o tempo restante e não expira a ação enquanto o teclado está nela.
  useEffect(() => {
    if (paused) return;
    const started = performance.now();
    const timer = window.setTimeout(() => dismiss(entry.id), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (performance.now() - started));
    };
  }, [dismiss, entry.id, paused]);

  return (
    <article
      role={urgent ? 'alert' : 'status'}
      aria-live={urgent ? 'assertive' : 'polite'}
      aria-atomic="true"
      data-variant={entry.variant}
      data-testid={`toast-${entry.variant}`}
      className="toast-item"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      onKeyDown={(event) => { if (event.key === 'Escape') dismiss(entry.id); }}
    >
      <Icon className="toast-icon" aria-hidden="true" />
      <div className="toast-copy">
        <span className="sr-only">{label}: </span>
        <p className="toast-title">{entry.title}</p>
        {entry.description && <p className="toast-description">{entry.description}</p>}
        {entry.action && (
          <button type="button" className="toast-action" onClick={() => {
            if (actionExecuted.current) return;
            actionExecuted.current = true;
            entry.action?.onClick();
            dismiss(entry.id);
          }}>{entry.action.label}</button>
        )}
      </div>
      <button type="button" className="toast-dismiss" onClick={() => dismiss(entry.id)} aria-label={`Dispensar: ${entry.title}`}>
        <X size={16} aria-hidden="true" />
      </button>
    </article>
  );
}

export function ToastProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const prefix = useId();
  const sequence = useRef(0);
  const dismiss = useCallback((id: string) => setEntries((current) => current.filter((entry) => entry.id !== id)), []);
  const toast = useCallback((options: ToastOptions) => {
    const id = `${prefix}-${++sequence.current}`;
    const duration = duracaoDoToast(options);
    setEntries((current) => [...current, {
      ...options, id, variant: options.variant ?? 'info',
      duration,
    }].slice(-MAX_VISIBLE));
    return id;
  }, [prefix]);
  // A API estável evita refazer efeitos de consumidores quando um toast entra ou sai.
  const api = useMemo<ToastApi>(() => ({
    toast, dismiss,
    success: (options) => toast({ ...options, variant: 'success' }),
    info: (options) => toast({ ...options, variant: 'info' }),
    warning: (options) => toast({ ...options, variant: 'warning' }),
    error: (options) => toast({ ...options, variant: 'error' }),
  }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <section aria-label="Notificações da plataforma" className="toast-viewport" data-testid="toast-viewport">
          {entries.map((entry) => <ToastItem key={entry.id} entry={entry} dismiss={dismiss} />)}
        </section>, document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast deve ser usado dentro de ToastProvider.');
  return context;
}
